import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Radio, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { publicApi } from '@/lib/api';
import { NavItem, Page } from '@/types';
import { isNavPathActive, renderNavIcon } from '@/components/nav/navIcons';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [pageNavItems, setPageNavItems] = useState<NavItem[]>([]);
  const [businessDirectoryPublic, setBusinessDirectoryPublic] = useState(false);
  const [logoCircleUrl, setLogoCircleUrl] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const loadNav = async () => {
      try {
        const [navRes, pagesRes, settingsRes] = await Promise.allSettled([
          publicApi.nav.getAll(),
          publicApi.pages.getNav(),
          publicApi.settings.get(),
        ]);

        if (navRes.status === 'fulfilled') {
          setNavItems(navRes.value.data);
        }

        if (pagesRes.status === 'fulfilled') {
          const pages: Page[] = pagesRes.value.data;
          const mapped: NavItem[] = pages.map((p) => ({
            id: p.id,
            label: p.title,
            url: `/${p.slug}`,
            displayOrder: p.navOrder ?? 0,
            icon: null,
            // Header currently renders only top-level items; pages are top-level by nature.
            children: [],
          }));
          setPageNavItems(mapped);
        }
        
        if (settingsRes.status === 'fulfilled') {
          // Prefer new circle logo URL; fall back to legacy logoUrl when needed.
          const s = settingsRes.value.data;
          setBusinessDirectoryPublic(s.businessDirectoryPublic === true);
          setLogoCircleUrl(s.logoCircleUrl || s.logoUrl || null);

          // Load font CSS dynamically if the admin configured a custom webfont.
          if (s.logoFontCssUrl && typeof document !== 'undefined') {
            const existing = document.getElementById('logo-font-css') as HTMLLinkElement | null;
            if (!existing) {
              const link = document.createElement('link');
              link.id = 'logo-font-css';
              link.rel = 'stylesheet';
              link.href = s.logoFontCssUrl;
              document.head.appendChild(link);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load navigation:', error);
      }
    };
    loadNav();
  }, []);

  const coreNav: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { id: -1, label: 'Home', url: '/', icon: 'home', displayOrder: -300, children: [] },
      { id: -2, label: 'Events', url: '/events', icon: 'calendar', displayOrder: -200, children: [] },
      { id: -3, label: 'News', url: '/blog', icon: 'newspaper', displayOrder: -100, children: [] },
      { id: -4, label: 'Photos', url: '/photos', icon: 'images', displayOrder: -95, children: [] },
      { id: -6, label: 'Podcast', url: '/podcast', icon: 'mic', displayOrder: -93, children: [] },
    ];
    if (businessDirectoryPublic) {
      items.push({
        id: -5,
        label: 'Directory',
        url: '/directory',
        icon: 'store',
        displayOrder: -90,
        children: [],
      });
    }
    return items;
  }, [businessDirectoryPublic]);

  const mergedByUrl = new Map<string, NavItem>();

  // Keep core links pinned first; then append admin nav + page nav unless already present.
  [...coreNav, ...navItems, ...pageNavItems].forEach((item) => {
    const urlKey = (item.url || '').trim().toLowerCase();
    if (!urlKey || mergedByUrl.has(urlKey)) return;
    mergedByUrl.set(urlKey, item);
  });

  const displayNav = Array.from(mergedByUrl.values()).sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return (
    <header className="glass-panel border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              {logoCircleUrl ? (
                <img 
                  src={logoCircleUrl} 
                  alt="GayPHX logo" 
                  className="w-full h-full object-contain"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    // Fallback to icon if image fails to load
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement?.classList.add('logo-fallback');
                  }}
                />
              ) : (
                <Radio className="w-5 h-5 text-white" />
              )}
            </div>
            {/* Always show the text logo */}
            <span className="font-logo text-2xl font-bold text-white">
              Gay<span className="text-primary">PHX</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {displayNav.map((item) => (
              <Link
                key={item.id}
                to={item.url}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  isNavPathActive(location.pathname, item.url)
                    ? 'bg-primary/20 text-primary'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                )}
              >
                {renderNavIcon(item.icon)}
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-white/5"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-white/10">
            {displayNav.map((item) => (
              <Link
                key={item.id}
                to={item.url}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                  isNavPathActive(location.pathname, item.url)
                    ? 'bg-primary/20 text-primary'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {renderNavIcon(item.icon)}
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
